// Maison Cellulaire shop layer: detailed product cards, a shop drawer per sport and a product view.
// The prototype reads the assets/products.json snapshot; on the Shopify theme (window.MC_LIVE) it reads the shop live.
import { catalogue } from './mc-catalogue.js?v=20261028';
const data = window.MC_LIVE ? await catalogue('') : await (await fetch('assets/products.json')).json();
const P = Object.fromEntries(data.all.map(p => [p.handle, p]));
const SHOP = data.shop;

export const SPORTS = {
  padel: { name: 'Padel', station: 'padel', parts: [['padel', 'Padel']],
    intro: 'Carbon rackets from 12K to 24K, overgrips, covers and a quilted bag. New to padel? Choose a round head, a light weight and a soft core.' },
  tennis: { name: 'Tennis', station: 'tennis', parts: [['tennis', 'Tennis']],
    intro: 'Pressurised and training balls, junior rackets and kit for practice sessions and matches.' },
  racket: { name: 'Racket sports', station: 'hall', parts: [['badminton', 'Badminton'], ['pickleball', 'Pickleball'], ['table-tennis', 'Table tennis']],
    intro: 'Badminton rackets and shuttlecocks, pickleball paddle sets and balls, table tennis bats and three-star balls.' },
  golf: { name: 'Golf', station: 'golf', parts: [['golf', 'Golf']],
    intro: 'Practice nets, putting mats and swing trainers for home, plus tees, towels and divot tools for the course.' },
  boxing: { name: 'Sports tech', station: 'boxing', parts: [['sports-tech', 'Sports tech']],
    intro: 'Golf rangefinders, massage guns, heart-rate straps, smartwatches, smart rings, open-ear earbuds and a radar bike light.' },
  gym: { name: 'Gym & yoga', station: 'gym', parts: [['gym-fitness', 'Gym & fitness'], ['yoga-activewear', 'Yoga & activewear'], ['other', 'Training wear']],
    intro: 'Resistance bands, weighted ropes, ab rollers, supports, yoga mats and activewear for a full home workout.' },
  football: { name: 'Run, ride & football', station: 'football', parts: [['running-cycling', 'Running & cycling'], ['football', 'Football']],
    intro: 'Touchscreen gloves, polarised sunglasses, neck warmers, bike phone mounts and swim goggles, plus grip socks and ladders for football.' },
};
const handlesOf = key => key === 'other' ? data.other : data.collections[key] || [];
const sportOf = handle => Object.entries(SPORTS).find(([, s]) => s.parts.some(([k]) => handlesOf(k).includes(handle)))?.[0];

// ---------- formatting ----------
const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const money = v => '£' + v.toFixed(2);
const img = (src, w) => src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '';
const srcset = src => [400, 800, 1200, 1600].map(w => `${img(src, w)} ${w}w`).join(', ');
// the second card photo only shows on hover, so touch screens never need it
const HOVER = matchMedia('(hover: hover)').matches;
const split = t => { const [a, ...b] = t.split(' – '); return [a, b.join(' – ')]; };
const optName = n => ({ Color: 'Colour', color: 'Colour', Colour: 'Colour' }[n] || n);
const optSummary = p => p.options.filter(o => o.values.length > 1 && !/ships from/i.test(o.name)).map(o => {
  const n = optName(o.name).toLowerCase();
  return `${o.values.length} ${n === 'colour' ? 'colours' : n === 'size' ? 'sizes' : n + ' options'}`;
}).join(' · ');
const priceHtml = p => {
  const range = p.priceMax > p.price ? `${money(p.price)} – ${money(p.priceMax)}` : money(p.price);
  const was = p.compare && p.compare > p.priceMax ? ` <s>${money(p.compare)}</s>` : '';
  return range + was;
};

// ---------- cards ----------
export function card(p) {
  const [name, sub] = split(p.title);
  const opts = optSummary(p);
  const sale = p.compare && p.compare > p.priceMax;
  return `<a class="card" href="${SHOP}/products/${p.handle}" data-product="${p.handle}">
    <span class="img">
      <img loading="lazy" src="${img(p.image, 800)}" srcset="${srcset(p.image)}" sizes="(max-width: 760px) 70vw, 300px" alt="${esc(name)}">
      ${p.images[1] && HOVER ? `<img class="alt" loading="lazy" src="${img(p.images[1], 800)}" srcset="${srcset(p.images[1])}" sizes="300px" alt="">` : ''}
      <span class="badges">${sale ? '<i>Offer</i>' : ''}${p.images.length > 2 ? `<i>${p.images.length} photos</i>` : ''}</span>
    </span>
    <span class="info">
      <span class="t">${esc(name)}</span>
      ${sub ? `<span class="sub">${esc(sub)}</span>` : ''}
      ${p.features.length ? `<span class="f">${p.features.slice(0, 2).map(f => `<span>${esc(f.split(/[:,.–(]/)[0].trim())}</span>`).join('')}</span>` : ''}
      ${opts ? `<span class="o">${opts}</span>` : ''}
      <span class="row"><span class="p">${priceHtml(p)}</span><span class="qv">Details</span></span>
    </span></a>`;
}

function fillRails() {
  for (const el of document.querySelectorAll('[data-rail]')) {
    const keys = el.dataset.rail.split(',');
    const list = keys.flatMap(handlesOf).map(h => P[h]);
    const sport = el.dataset.sport || sportOf(list[0]?.handle);
    const label = el.dataset.label || '';
    el.innerHTML = `${label ? `<h4>${esc(label)}</h4>` : ''}<div class="track">${list.map(card).join('')}</div>
      <button class="all" data-open-sport="${sport}" data-part="${keys.length === 1 ? keys[0] : ''}">See all ${list.length} in detail →</button>`;
  }
  const grid = document.querySelector('[data-grid="all"]');
  if (grid) grid.innerHTML = data.all.map(card).join('');
  for (const t of document.querySelectorAll('.sport-tiles [data-open-sport]')) {
    const n = new Set(SPORTS[t.dataset.openSport]?.parts.flatMap(([k]) => handlesOf(k))).size;
    t.querySelector('span').textContent = `${n} product${n === 1 ? '' : 's'}`;
  }
}

// ---------- layers ----------
const layers = [];
function openLayer(el) {
  el.hidden = false; requestAnimationFrame(() => el.classList.add('open'));
  if (!layers.includes(el)) layers.push(el);
  document.documentElement.classList.add('mc-locked');
}
function closeLayer(el = layers.at(-1)) {
  if (!el) return;
  el.classList.remove('open'); setTimeout(() => { if (!el.classList.contains('open')) el.hidden = true; }, 350);
  layers.splice(layers.indexOf(el), 1);
  if (!layers.length) document.documentElement.classList.remove('mc-locked');
}

// ---------- sport drawer ----------
const drawer = document.createElement('aside');
drawer.className = 'mc-drawer'; drawer.hidden = true;
drawer.innerHTML = `<div class="scrim" data-close></div><div class="panel" role="dialog" aria-modal="true" aria-label="Shop by sport">
  <header><nav class="sports"></nav><button class="x" data-close aria-label="Close">×</button></header>
  <div class="body"></div></div>`;
document.body.append(drawer);
let state = { sport: 'padel', part: '', sort: 'featured' };

function renderDrawer() {
  const s = SPORTS[state.sport];
  drawer.querySelector('.sports').innerHTML = Object.entries(SPORTS).map(([k, v]) =>
    `<button class="${k === state.sport ? 'on' : ''}" data-open-sport="${k}">${esc(v.name)}</button>`).join('');
  const parts = s.parts.filter(([k]) => handlesOf(k).length);
  let list = (state.part ? handlesOf(state.part) : parts.flatMap(([k]) => handlesOf(k))).map(h => P[h]);
  if (state.sort === 'low') list = [...list].sort((a, b) => a.price - b.price);
  if (state.sort === 'high') list = [...list].sort((a, b) => b.price - a.price);
  const total = parts.reduce((n, [k]) => n + handlesOf(k).length, 0);
  const lo = Math.min(...list.map(p => p.price)), hi = Math.max(...list.map(p => p.priceMax));
  drawer.querySelector('.body').innerHTML = `
    <div class="kicker">Shop · ${total} products</div>
    <h2>${esc(s.name)}</h2>
    <p class="lede">${esc(s.intro)}</p>
    <div class="tools">
      ${parts.length > 1 ? `<div class="tabs"><button class="${!state.part ? 'on' : ''}" data-part-tab="">All ${total}</button>${parts.map(([k, n]) =>
        `<button class="${state.part === k ? 'on' : ''}" data-part-tab="${k}">${esc(n)} ${handlesOf(k).length}</button>`).join('')}</div>` : '<div></div>'}
      <label class="sort">Sort <select data-sort>
        <option value="featured"${state.sort === 'featured' ? ' selected' : ''}>Featured</option>
        <option value="low"${state.sort === 'low' ? ' selected' : ''}>Price, low to high</option>
        <option value="high"${state.sort === 'high' ? ' selected' : ''}>Price, high to low</option></select></label>
    </div>
    <p class="meta">${list.length} shown · ${money(lo)} to ${money(hi)} · free tracked delivery · 14-day returns</p>
    <div class="grid">${list.map(card).join('')}</div>
    <a class="btn" href="${SHOP}/collections/${state.part || parts[0][0]}">Open the ${esc(s.name.toLowerCase())} collection page</a>`;
  drawer.querySelector('.panel').scrollTop = 0;
}
export function openSport(sport, part = '') {
  if (!SPORTS[sport]) return;
  state = { ...state, sport, part };
  // move the 3D camera to that sport's venue behind the drawer
  document.querySelector(`[data-mc-station="${SPORTS[sport].station}"]`)?.scrollIntoView({ behavior: 'instant', block: 'start' });
  renderDrawer(); openLayer(drawer);
}

// ---------- product view ----------
const pdp = document.createElement('aside');
pdp.className = 'mc-pdp'; pdp.hidden = true;
pdp.innerHTML = `<div class="scrim" data-close></div><div class="panel" role="dialog" aria-modal="true" aria-label="Product details">
  <button class="x" data-close aria-label="Close">×</button><div class="body"></div></div>`;
document.body.append(pdp);

export function openProduct(handle) {
  const p = P[handle]; if (!p) return;
  const [name, sub] = split(p.title);
  const sport = sportOf(handle);
  const sel = [...(p.variants.find(v => v.available) || p.variants[0]).options];
  const related = sport ? SPORTS[sport].parts.flatMap(([k]) => handlesOf(k)).filter(h => h !== handle).slice(0, 8).map(h => P[h]) : [];
  pdp.querySelector('.body').innerHTML = `
    <div class="gallery">
      <div class="main"><img alt="${esc(name)}" src="${img(p.image, 1600)}" srcset="${srcset(p.image)}" sizes="(max-width: 900px) 100vw, 50vw"></div>
      <div class="thumbs">${p.images.map((src, i) => `<button class="${i ? '' : 'on'}" data-img="${src}"><img loading="lazy" src="${img(src, 200)}" alt=""></button>`).join('')}</div>
    </div>
    <div class="detail">
      <div class="kicker">${sport ? esc(SPORTS[sport].name) : 'Maison Cellulaire'} · ${esc(p.vendor)}</div>
      <h2>${esc(name)}</h2>
      ${sub ? `<p class="sub">${esc(sub)}</p>` : ''}
      <div class="price"></div>
      ${p.options.map((o, i) => `<div class="opt"><h4>${esc(optName(o.name))} <span data-opt-val="${i}"></span></h4><div class="vals">${o.values.map(v =>
        `<button data-opt="${i}" data-val="${esc(v)}">${esc(v)}</button>`).join('')}</div></div>`).join('')}
      <div class="buy">
        <div class="qty"><button data-q="-1" aria-label="Less">−</button><span>1</span><button data-q="1" aria-label="More">+</button></div>
        <a class="btn solid add" href="#">Add to basket</a>
      </div>
      <a class="more" href="${SHOP}/products/${p.handle}">Open the full product page →</a>
      <p class="offer">First order? Use code <b>WELCOME10</b> at checkout for 10% off.</p>
      <ul class="trust"><li>Free tracked delivery, 7–15 working days</li><li>14-day returns</li><li>Secure checkout</li></ul>
      <div class="desc">${p.body || `<p>${esc(p.title)}</p>`}</div>
    </div>
    ${related.length ? `<div class="related"><h4>More ${esc(SPORTS[sport].name.toLowerCase())}</h4><div class="track">${related.map(card).join('')}</div>
      <button class="all" data-open-sport="${sport}">See everything in ${esc(SPORTS[sport].name.toLowerCase())} →</button></div>` : ''}`;
  let qty = 1;
  const body = pdp.querySelector('.body');
  const show = src => {
    const m = body.querySelector('.main img'); m.src = img(src, 1600); m.srcset = srcset(src);
    body.querySelectorAll('.thumbs button').forEach(b => b.classList.toggle('on', b.dataset.img === src));
  };
  const update = (swapImage) => {
    const v = p.variants.find(v => v.options.every((o, i) => o === sel[i])) || p.variants[0];
    body.querySelectorAll('[data-opt]').forEach(b => {
      const i = +b.dataset.opt, val = b.dataset.val;
      b.classList.toggle('on', sel[i] === val);
      const tryOpts = sel.map((s, j) => j === i ? val : s);
      const match = p.variants.find(x => x.options.every((o, j) => o === tryOpts[j]));
      b.classList.toggle('na', !match || !match.available);
    });
    body.querySelectorAll('[data-opt-val]').forEach(s => s.textContent = sel[+s.dataset.optVal] || '');
    const was = v.compare && v.compare > v.price ? `<s>${money(v.compare)}</s><em>Save ${money(v.compare - v.price)}</em>` : '';
    body.querySelector('.price').innerHTML = `<b>${money(v.price)}</b>${was}<span class="${v.available ? 'in' : 'out'}">${v.available ? 'In stock' : 'Sold out'}</span>`;
    const add = body.querySelector('.add');
    add.href = `${SHOP}/cart/add?id=${v.id}&quantity=${qty}`;
    add.classList.toggle('disabled', !v.available);
    add.textContent = v.available ? `Add to basket · ${money(v.price * qty)}` : 'Sold out';
    if (swapImage && v.image) show(v.image);
  };
  body.addEventListener('click', e => {
    const o = e.target.closest('[data-opt]'), t = e.target.closest('[data-img]'), q = e.target.closest('[data-q]');
    if (o) { sel[+o.dataset.opt] = o.dataset.val; update(true); }
    if (t) show(t.dataset.img);
    if (q) { qty = Math.max(1, Math.min(10, qty + +q.dataset.q)); body.querySelector('.qty span').textContent = qty; update(false); }
  });
  update(true);
  pdp.querySelector('.panel').scrollTop = 0;
  openLayer(pdp);
}

// ---------- wiring ----------
document.addEventListener('click', e => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
  const prod = e.target.closest('[data-product]');
  if (prod) { e.preventDefault(); openProduct(prod.dataset.product); return; }
  const sp = e.target.closest('[data-open-sport]');
  if (sp) { e.preventDefault(); if (pdp.classList.contains('open')) closeLayer(pdp); openSport(sp.dataset.openSport, sp.dataset.part || ''); return; }
  const tab = e.target.closest('[data-part-tab]');
  if (tab) { state.part = tab.dataset.partTab; renderDrawer(); return; }
  if (e.target.closest('[data-close]')) closeLayer(e.target.closest('.mc-drawer, .mc-pdp'));
});
document.addEventListener('change', e => { if (e.target.matches('[data-sort]')) { state.sort = e.target.value; renderDrawer(); } });
document.addEventListener('keydown', e => { if (e.key === 'Escape') closeLayer(); });

fillRails();
const q = new URLSearchParams(location.search);
if (q.get('sport')) openSport(q.get('sport'));
if (q.get('product')) openProduct(q.get('product'));
